/* Softbloom cart drawer — self-contained, no global.js dependency. */
if (!customElements.get('sb-cart-drawer')) {
  customElements.define(
    'sb-cart-drawer',
    class SbCartDrawer extends HTMLElement {
      connectedCallback() {
        this.overlay = this.querySelector('[data-sb-cart-overlay]');
        this.panel = this.querySelector('.sb-cart__panel');
        this.lastFocus = null;

        this.querySelectorAll('[data-sb-cart-close]').forEach((el) =>
          el.addEventListener('click', () => this.close())
        );
        if (this.overlay) this.overlay.addEventListener('click', () => this.close());

        this.onKeydown = this.onKeydown.bind(this);

        // Opened by sb-product-form after a successful add.
        document.addEventListener('sb:cart:open', () => this.open());
        // Refresh content from a Section Rendering API payload.
        document.addEventListener('sb:cart:render', (e) => this.render(e.detail && e.detail.html));

        // Line-item actions (delegated so they survive re-renders).
        this.addEventListener('click', (e) => {
          const removeBtn = e.target.closest('[data-sb-cart-remove]');
          if (removeBtn) {
            e.preventDefault();
            this.changeLine(removeBtn.getAttribute('data-sb-cart-remove'), 0, removeBtn.closest('.sb-cart__item'));
            return;
          }

          const stepBtn = e.target.closest('[data-sb-cart-step]');
          if (stepBtn) {
            e.preventDefault();
            const input = stepBtn.parentElement.querySelector('[data-sb-cart-qty-input]');
            if (!input) return;
            const delta = stepBtn.getAttribute('data-sb-cart-step') === 'up' ? 1 : -1;
            const next = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
            this.changeLine(input.getAttribute('data-sb-cart-qty-input'), next, stepBtn.closest('.sb-cart__item'));
          }
        });

        // Quantity typed directly into the input.
        this.addEventListener('change', (e) => {
          const input = e.target.closest('[data-sb-cart-qty-input]');
          if (!input) return;
          const next = Math.max(0, parseInt(input.value, 10) || 0);
          this.changeLine(input.getAttribute('data-sb-cart-qty-input'), next, input.closest('.sb-cart__item'));
        });
      }

      async changeLine(key, quantity, item) {
        if (item) {
          item.style.opacity = '0.5';
          item.querySelectorAll('button, input').forEach((el) => (el.disabled = true));
        }
        try {
          const res = await fetch(window.routes.cart_change_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify({
              id: key,
              quantity: quantity,
              sections: ['sb-cart-drawer'],
              sections_url: window.location.pathname,
            }),
          });
          const data = await res.json();
          const html = data.sections && data.sections['sb-cart-drawer'];
          if (html) this.render(html);
        } catch (err) {
          // Fall back to the full cart page if the AJAX change fails.
          window.location.href = window.routes.cart_url;
        }
      }

      open() {
        this.lastFocus = document.activeElement;
        this.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeydown);
        // Focus the panel for screen readers / keyboard users.
        window.requestAnimationFrame(() => this.panel && this.panel.focus());
      }

      close() {
        this.classList.remove('is-open');
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.onKeydown);
        if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
      }

      onKeydown(e) {
        if (e.key === 'Escape') {
          this.close();
          return;
        }
        if (e.key !== 'Tab') return;

        const focusables = this.getFocusable();
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      getFocusable() {
        return Array.from(
          this.panel.querySelectorAll(
            'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => el.offsetParent !== null);
      }

      /* Replace the inner content with freshly-rendered section HTML. */
      render(html) {
        if (!html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const next = parsed.querySelector('[data-sb-cart-inner]');
        const current = this.querySelector('[data-sb-cart-inner]');
        if (next && current) current.innerHTML = next.innerHTML;

        const nextRoot = parsed.querySelector('sb-cart-drawer');
        if (nextRoot && nextRoot.dataset.itemCount != null) {
          this.dataset.itemCount = nextRoot.dataset.itemCount;
          document.dispatchEvent(
            new CustomEvent('sb:cart:count', { detail: { count: Number(nextRoot.dataset.itemCount) } })
          );
        }
      }
    }
  );
}

/* Any element with [data-sb-cart-open] opens the drawer (e.g. the global cart toggle). */
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-sb-cart-open]')) {
    e.preventDefault();
    document.dispatchEvent(new CustomEvent('sb:cart:open'));
  }
});
